import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1 } from "iconsax-react";
import { useAppSelector } from "@/shared/hooks";
import { Button, ModalForm } from "@/shared/ui";
import { formatDateForContract } from "@/shared/utils";
import type { WorkerListItem } from "@/features/hr/employees";
import type { CreateLeaveFormValues } from "../validation";

interface Props {
  employee: WorkerListItem;
  formData: CreateLeaveFormValues;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  isPreviewOnly?: boolean;
  recipientLabel?: string;
  fromLabel?: string;
  applicationTitle?: string;
  signatureLabel?: string;
  children: React.ReactNode;
}

export default function LeavePreviewLayout({
  employee,
  formData,
  onClose,
  onSubmit,
  isLoading,
  isPreviewOnly = false,
  recipientLabel,
  fromLabel,
  applicationTitle,
  signatureLabel,
  children,
}: Props) {
  const { t, i18n } = useTranslation("LeaveApplicationsPage");
  const userData = useAppSelector(function (state) {
    return state.auth.data?.user;
  });

  const dateLocale = i18n.language === "kk" ? "kk" : "ru";

  const contractDate = useMemo(
    function () {
      return formatDateForContract(new Date(), dateLocale);
    },
    [dateLocale],
  );

  const organizationName = userData?.organization?.name || "_____________";
  const employerFullName = userData?.organization?.chief_name || "_____________";
  const employeeFullName = employee.full_name || "_____________";
  const employeeJobPosition =
    employee.active_contract?.job_position_ru || employee.active_contract?.work_position || "_____________";

  const resolvedRecipientLabel = recipientLabel ?? t("preview.shared.toDirector");
  const resolvedFromLabel = fromLabel ?? t("preview.shared.from");
  const resolvedApplicationTitle = applicationTitle ?? t("preview.shared.application");
  const resolvedSignatureLabel = signatureLabel ?? t("preview.shared.signature");

  return (
    <ModalForm
      icon={DocumentText1}
      onClose={onClose}
      resize
      footer={
        isPreviewOnly ? (
          <Button variant="tertiary" size="huge" className="w-full" onClick={onClose}>
            {t("preview.close")}
          </Button>
        ) : (
          <div className="grid grid-cols-[2fr_3fr] gap-3">
            <Button variant="secondary" size="huge" onClick={onClose} disabled={isLoading}>
              {t("preview.back")}
            </Button>
            <Button variant="primary" size="huge" disabled={isLoading} onClick={onSubmit}>
              {isLoading ? t("preview.submitting") : t("preview.submit")}
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col h-full">
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("preview.title")}</h4>
        </div>

        <div className="flex-1 overflow-auto page-scroll pr-5 pt-5">
          <div className="flex flex-col text-body-regular-md content-base-primary surface-base-fill">
            <div className="text-right mb-6">
              <p>
                <strong>{resolvedRecipientLabel}</strong>
              </p>
              <p>
                <strong>{organizationName}</strong>
              </p>
              <p>
                <strong>{employerFullName}</strong>
              </p>
              <p>
                {resolvedFromLabel} <strong>{employeeJobPosition}</strong>
              </p>
              <p>
                <strong>{employeeFullName}</strong>
              </p>
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-4">{resolvedApplicationTitle}</h1>
            </div>

            <div className="mb-6 text-justify">{children}</div>

            <div className="flex justify-end mt-8 mb-6">
              <div>
                <div className="border-t border-black mt-8 mb-2 w-48"></div>
                <p className="mb-1">
                  <strong>{resolvedSignatureLabel}</strong>
                </p>
                <p className="mb-1">
                  <strong>{employeeFullName}</strong>
                </p>
                <p className="text-sm">
                  <strong>{contractDate}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalForm>
  );
}
